import 'tippy.js/dist/tippy.css'
import 'tippy.js/animations/shift-away.css'
import 'tippy.js/themes/material.css'

import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type Row,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import clsx from 'clsx'
import {
  ArchiveX,
  CheckIcon,
  ChevronDown,
  ChevronUp,
  Clipboard,
  EllipsisVertical,
  FilePenLine,
  Filter,
  Pencil,
  Send,
  UserPlus,
  UserRound,
  UserRoundPen,
} from 'lucide-react'
import React, { createContext, useContext, useEffect } from 'react'
import tippy from 'tippy.js'

import { employersEmployees } from 'rails_routes'
import { Button, Tooltip, useBreakpoint } from 'slab'

import { type EmployeeTableRow, type EnrollmentEvent } from '../types'
import { handleStatusHover } from '../utils'
import {
  CreateOrEditEmployee,
  CreateOrEditEmployeeProvider,
  useCreateOrEditEmployee,
} from './CreateOrEditEmployee'
import {
  CreateOrEditEnrollmentEvent,
  CreateOrEditEnrollmentEventProvider,
  useCreateOrEditEnrollmentEvent,
} from './CreateOrEditEnrollmentEvent'
import { EmployeeReportsDropdown } from './EmployeeReportsDropdown'
import EnrollmentRateTracker from './EnrollmentRateTracker'
import OffboardEmployeeDialog from './OffboardEmployeeDialog'
import PayrollSyncStatusHeader from './PayrollSyncStatusHeader'
import TanstackTable from './reusable/table/TanstackTable'
import { TanstackTableGlobalSearch } from './reusable/table/TanstackTableGlobalSearch'
import TooltipWrapper from './reusable/TooltipWrapper'
import { SelectPlanYear } from './SelectPlanYearDropdown'
import {
  DropdownMenu as DropdownMenuContainer,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'

const enrollmentStatusOrder = [
  'not_invited',
  'invite_pending',
  'invited',
  'in_progress',
  'enrolled',
  'declined',
  'expired',
  'payroll_deactivated',
  'cobra',
  'termination_scheduled',
  'terminated',
]

const isEmployeeInvitedOrInEnrollmentProcess = (enrollmentStatus: string): boolean => {
  const invitedStatuses = [
    'invite_pending',
    'invited',
    'in_progress',
    'enrolled',
    'declined',
    'expired',
    'cobra',
    'termination_scheduled',
  ]
  return invitedStatuses.includes(enrollmentStatus)
}

type EnrollmentStatusFilters = {
  not_invited: boolean
  invite_pending: boolean
  invited: boolean
  pending_automated_invite: boolean
  in_progress: boolean
  enrolled: boolean
  declined: boolean
  expired: boolean
  payroll_deactivated: boolean
  cobra: boolean
  termination_scheduled: boolean
  terminated: boolean
}

type SelectedRowIdsType = {
  [key: string]: boolean
}

type EmployeesTableContextType = {
  hideAddEmployeeButton: boolean
  isFullyOnboarded: boolean
  isFullyOnboardedWithBankAccount: boolean
  isDeniedEmployer: boolean
  hasPayrollIntegration: boolean
  hasInvitedEmployeesBefore: boolean
  planYearsAvailable: number[]
  hasMembersWithActiveCoverageThisYear: boolean
  setSelectedRowIds: React.Dispatch<
    React.SetStateAction<{
      [key: string]: boolean
    }>
  >
  selectedRowIds: SelectedRowIdsType
  allEmployees: EmployeeTableRow[]
  allowedEmploymentSubtypesByYear: { [key: number]: string[] }
  selectedPlanYear: number
  handleResetSelectedRows: () => void
  employeeSubtypesToInclude: string[]
  setFilters: React.Dispatch<React.SetStateAction<EnrollmentStatusFilters>>
  filters: EnrollmentStatusFilters
  setEmployeeToOffboard: React.Dispatch<React.SetStateAction<any>>
  employmentSubtypeOptions: string[]
  setEmployeeSubtypesToInclude: React.Dispatch<React.SetStateAction<string[]>>
  fetchEmployees: () => void
}

const EmployeesTableContext = createContext<EmployeesTableContextType>({
  hideAddEmployeeButton: false,
  isFullyOnboarded: false,
  isFullyOnboardedWithBankAccount: false,
  isDeniedEmployer: true,
  hasPayrollIntegration: false,
  hasInvitedEmployeesBefore: false,
  planYearsAvailable: [new Date().getFullYear()],
  hasMembersWithActiveCoverageThisYear: false,
  setSelectedRowIds: () => { },
  selectedRowIds: {},
  allEmployees: [],
  allowedEmploymentSubtypesByYear: {},
  selectedPlanYear: new Date().getFullYear(),
  handleResetSelectedRows: () => { },
  employeeSubtypesToInclude: [],
  setFilters: () => { },
  filters: {} as EnrollmentStatusFilters,
  setEmployeeToOffboard: () => { },
  employmentSubtypeOptions: [],
  setEmployeeSubtypesToInclude: () => { },
  fetchEmployees: () => { },
})

const handleCopyInviteLinkClick = (event: any, inviteLink: any) => {
  event.stopPropagation()
  if (!event.target._tippy) {
    tippy(event.target, {
      content: 'Copied!',
      theme: 'material',
      animation: 'shift-away',
      arrow: true,
      placement: 'bottom',
      trigger: 'click',
      showOnCreate: true,
      onShow (instance) {
        setTimeout(() => {
          instance.hide()
        }, 2000)
      },
    })
  }
  navigator.clipboard.writeText(inviteLink)
}

const EditInviteMenuItemTooltip = ({
  children,
  isNotInvited,
  isDisabled,
  enrollmentEvent,
  hasMembersWithActiveCoverageThisYear,
}: {
  children: React.ReactNode
  isNotInvited: boolean
  isDisabled: boolean
  enrollmentEvent?: EnrollmentEvent
  hasMembersWithActiveCoverageThisYear: boolean
}) => {
  let tooltipText = 'Edit pending invite'
  if (isNotInvited) {
    tooltipText = 'There is no invite to edit for this employee'
  } else if (
    enrollmentEvent?.enrollment_type === 'open_enrollment' &&
    hasMembersWithActiveCoverageThisYear
  ) {
    tooltipText = 'Cannot edit invites for open enrollment'
  } else if (isDisabled) {
    tooltipText = 'Cannot edit already sent invite'
  }

  if (!isDisabled) {
    return <>{children}</>
  }

  return (
    <Tooltip content={tooltipText} side='bottom' zIndex={100}>
      <div>{children}</div>
    </Tooltip>
  )
}

const CopyInviteMenuItemTooltip = ({
  children,
  isNotInvited,
  isDisabled,
}: {
  children: React.ReactNode
  isNotInvited: boolean
  isDisabled: boolean
}) => {
  const tooltipText = isNotInvited
    ? 'Please invite this employee before sending an invite link'
    : isDisabled
      ? 'Cannot copy invite after employee has begun onboarding'
      : 'Copy invite link'

  if (!isDisabled) {
    return <>{children}</>
  }

  return (
    <Tooltip content={tooltipText} side='bottom' zIndex={100}>
      <div>{children}</div>
    </Tooltip>
  )
}

const AddEmployeeTooltip = ({
  isFullyOnboarded,
  hasPayrollIntegration,
  isDeniedEmployer,
  children,
}: {
  isFullyOnboarded: boolean
  hasPayrollIntegration: boolean
  isDeniedEmployer: boolean
  children: React.ReactNode
}) => {
  let tooltipText = ''
  if (!isFullyOnboarded) {
    tooltipText = 'You must complete onboarding to add and invite employees'
  } else if (hasPayrollIntegration) {
    tooltipText = `You cannot add employees while connected to your payroll provider.
    Please add new employees via your payroll. If you prefer to add employees manually
    and are no longer syncing your employer directory via your payroll provider, please
    disconnect your payroll via the Settings > Payroll page within your employer dashboard.`
  } else if (isDeniedEmployer) {
    tooltipText = 'Adding employees is disabled. Please reach out to support@thatch.com for help.'
  }

  return (
    <Tooltip content={tooltipText} side='bottom'>
      <div>{children}</div>
    </Tooltip>
  )
}

const InviteEmployeeTooltip = ({
  isFullyOnboarded,
  isFullyOnboardedWithBankAccount,
  isEmployeeInviteDisabled,
  isDeniedEmployer,
  allowancesSetUpForPlanYear,
  children,
}: {
  isFullyOnboarded: boolean
  isFullyOnboardedWithBankAccount: boolean
  isEmployeeInviteDisabled: boolean
  isDeniedEmployer: boolean
  allowancesSetUpForPlanYear: boolean
  children: React.ReactNode
}) => {
  let tooltipText = ''
  if (!isFullyOnboarded) {
    tooltipText = 'You must complete onboarding to add and invite employees'
  } else if (!isFullyOnboardedWithBankAccount) {
    tooltipText = 'You must have a verified bank account to invite employees'
  } else if (isDeniedEmployer) {
    tooltipText = 'Inviting employees is disabled. Please reach out to support@thatch.com for help.'
  } else if (!allowancesSetUpForPlanYear) {
    tooltipText = 'You must have allowances set up for the selected year to invite employees'
  } else if (isEmployeeInviteDisabled) {
    tooltipText = 'Please select an employee to invite them'
  }

  return (
    <Tooltip content={tooltipText} side='bottom'>
      <div>{children}</div>
    </Tooltip>
  )
}

const TerminateEmployeeMenuItem = ({ row }: { row: Row<EmployeeTableRow> }) => {
  const { setEmployeeToOffboard } = useContext(EmployeesTableContext)

  const handleOffboardEmployeeClick = (event: any, employee: any) => {
    event.stopPropagation()
    window.segment?.track('[ER] Offboard employee clicked', {
      employee_id: employee.id,
      employee_name: employee.first_name + ' ' + employee.last_name,
      employee_email: employee.personal_email || employee.work_email,
    })
    setEmployeeToOffboard(employee)
  }

  const enrollmentStatus = row.getValue('enrollment_status')
  if (
    enrollmentStatus === 'terminated' ||
    enrollmentStatus === 'termination_scheduled' ||
    enrollmentStatus === 'cobra'
  ) {
    return (
      <TooltipWrapper
        buttonClasses=''
        tooltipText="This employee has already been offboarded. If you would like to update this employee's termination information, please contact support@thatch.com."
      >
        <DropdownMenuItem disabled>
          <ArchiveX size={16} />
          <span>Offboard employee</span>
        </DropdownMenuItem>
      </TooltipWrapper>
    )
  }

  return (
    <DropdownMenuItem
      onClick={(e) => handleOffboardEmployeeClick(e, row.original as any)}
    >
      <ArchiveX size={16} />
      <span>Offboard employee</span>
    </DropdownMenuItem>
  )
}

const InviteEmployeeMenuItems = ({ row }: { row: Row<EmployeeTableRow> }) => {
  const { hasMembersWithActiveCoverageThisYear } = useContext(
    EmployeesTableContext
  )
  const { openCreateOrEditEnrollmentEvent } = useCreateOrEditEnrollmentEvent()

  const { invite_link: inviteLink } = row.original
  const enrollmentStatus: string = row.getValue('enrollment_status')
  const isNotInvited = enrollmentStatus === 'not_invited'
  const isCopyInviteDisabled = [
    'not_invited',
    'in_progress',
    'enrolled',
    'payroll_deactivated',
    'terminated',
    'cobra',
    'expired'
  ].includes(enrollmentStatus)

  const enrollmentEvent = row.original.enrollment_event
  const isEditEnrollmentEventDisabled =
    !enrollmentEvent ||
    enrollmentStatus !== 'invite_pending' ||
    (hasMembersWithActiveCoverageThisYear &&
      enrollmentEvent.enrollment_type === 'open_enrollment')

  return (
    <>
      <EditInviteMenuItemTooltip
        isNotInvited={isNotInvited}
        isDisabled={isEditEnrollmentEventDisabled}
        enrollmentEvent={enrollmentEvent}
        hasMembersWithActiveCoverageThisYear={
          hasMembersWithActiveCoverageThisYear
        }
      >
        <DropdownMenuItem
          disabled={isEditEnrollmentEventDisabled}
          onClick={() =>
            !isEditEnrollmentEventDisabled &&
            openCreateOrEditEnrollmentEvent([row.original], enrollmentEvent)}
        >
          <FilePenLine size={20} />
          <span>Edit invite</span>
        </DropdownMenuItem>
      </EditInviteMenuItemTooltip>

      <CopyInviteMenuItemTooltip
        isNotInvited={isNotInvited}
        isDisabled={isCopyInviteDisabled}
      >
        <DropdownMenuItem
          disabled={isCopyInviteDisabled}
          onClick={(e) =>
            !isCopyInviteDisabled && handleCopyInviteLinkClick(e, inviteLink)}
        >
          <Clipboard size={20} />
          <span>Copy invite link</span>
        </DropdownMenuItem>
      </CopyInviteMenuItemTooltip>
    </>
  )
}

const EMPLOYMENT_SUBTYPE_DICT: {
  [key: string]: { label: string; pillClass: string }
} = {
  full_time: {
    label: 'Full-time',
    pillClass: 'bg-green-100 text-green-700',
  },
  part_time: {
    label: 'Part-time',
    pillClass: 'bg-orange-100 text-orange-800',
  },
  not_specified: {
    label: 'Not specified',
    pillClass: 'bg-gray-100 text-gray-800',
  },
  intern: {
    label: 'Intern',
    pillClass: 'bg-teal-100 text-teal-800',
  },
  seasonal: {
    label: 'Seasonal',
    pillClass: 'bg-red-100 text-red-800',
  },
  temp: {
    label: 'Temp',
    pillClass: 'bg-yellow-100 text-yellow-800',
  },
  individual_contractor: {
    label: 'Individual Contractor',
    pillClass: 'bg-lime-100 text-lime-800',
  },
}

type AddEmployeeButtonProps = {
  isFullyOnboarded: boolean
  isDeniedEmployer: boolean
  hasPayrollIntegration: boolean
}

type EmployeesTableProps = AddEmployeeButtonProps & {
  isFullyOnboardedWithBankAccount: boolean
  employerName: string
  hideAddEmployeeButton: boolean
  hasInvitedEmployeesBefore: boolean
  planYearsAvailable: number[]
  hasMembersWithActiveCoverageThisYear: boolean
  allowedEmploymentSubtypesByYear: { [key: number]: string[] }
  payrollIntegrationProvider: string | null
  lastEmployeeSync: string | null
  nextEmployeeSync: string | null
  isConnectedToPayroll: boolean | null
  requiresEmployeeDeduplication: boolean
  automaticDeductionsSetupComplete: boolean
  automaticDeductionsEnabled: boolean
  showAutomatedDeductionsNotice: boolean
  supportsAutomaticDeductions: boolean
  baaRestrictedColumnsEnabled: boolean
  expiredStatusEnabled: boolean
}

const columnHelper = createColumnHelper<EmployeeTableRow>()

const createColumns = (expiredStatusEnabled: boolean, allowancesSetUpForPlanYear: boolean) => {
  return [columnHelper.display({
    id: 'select',
    header: ({ table }) => {
      const {
        setSelectedRowIds,
        hasPayrollIntegration,
        allowedEmploymentSubtypesByYear,
        isDeniedEmployer,
        selectedPlanYear,
        allEmployees,
        employeeSubtypesToInclude,
      } = useContext(EmployeesTableContext)
      return (
        <input
          id='select-all-checkbox'
          type='checkbox'
          checked={
            table.getIsAllPageRowsSelected() ||
            table.getIsSomePageRowsSelected()
          }
          onChange={() => {
            if (
              table.getIsAllPageRowsSelected() ||
              table.getIsSomePageRowsSelected()
            ) {
              setSelectedRowIds({})
            } else {
              const newSelected: { [key: string]: boolean } = {}
              allEmployees.forEach((employee) => {
                if (
                  employee.enrollment_status === 'not_invited' &&
                  selectedPlanYear >= new Date().getFullYear() &&
                  (allowedEmploymentSubtypesByYear[selectedPlanYear].includes(
                    employee.employment_subtype
                  ) ||
                    !hasPayrollIntegration) &&
                  (employeeSubtypesToInclude.length === 0 ||
                    employeeSubtypesToInclude.includes(
                      employee.employment_subtype
                    )) && !isDeniedEmployer && allowancesSetUpForPlanYear
                ) {
                  newSelected[employee.id] = true
                }
              })
              setSelectedRowIds(newSelected)
            }
          }}
          aria-label='Select all'
          className='mx-auto h-4 w-4 rounded border-gray-300 text-thatch-purple focus:ring-thatch-purple disabled:bg-slate-100'
        />
      )
    },
    cell: ({ row }) => {
      const {
        hasPayrollIntegration,
        isDeniedEmployer,
        allowedEmploymentSubtypesByYear,
        selectedPlanYear,
        setSelectedRowIds,
      } = useContext(EmployeesTableContext)

      const isPendingAutomatedInvite = row.getValue('enrollment_status') === 'pending_automated_invite'
      const isAlreadyInvited =
        row.getValue('enrollment_status') !== 'not_invited'
      const isForPastYear = selectedPlanYear < new Date().getFullYear()
      const hasInvalidSubtype = !allowedEmploymentSubtypesByYear[
        selectedPlanYear
      ].includes(row.getValue('employment_subtype'))
      if (isPendingAutomatedInvite || isAlreadyInvited || isForPastYear || (hasInvalidSubtype && hasPayrollIntegration) || isDeniedEmployer || !allowancesSetUpForPlanYear) {
        const input = (
          <input
            name='select-checkbox'
            type='checkbox'
            checked={false}
            aria-label='Select row'
            className='mx-auto h-4 w-4 rounded border-gray-300 bg-slate-200 cursor-not-allowed'
            disabled
          />
        )

        let tooltipText = 'Inviting employees is disabled. Please reach out to support@thatch.com for help.'
        if (isPendingAutomatedInvite) {
          tooltipText = 'This employee was marked as eligible during open enrollment renewals and will be invited automatically on November 5th, 2025. Please reach out to support@thatch.com if you need assistance.'
        } else if (isAlreadyInvited) {
          tooltipText = 'Cannot select an employee who has already been invited.'
        } else if (isForPastYear) {
          tooltipText = 'Cannot invite an employee to a plan year that has already passed. Please select a different year.'
        } else if (!allowancesSetUpForPlanYear) {
          tooltipText = 'Cannot invite employees if allowances are missing for the selected year.'
        } else if (hasInvalidSubtype && hasPayrollIntegration) {
          tooltipText = 'Cannot select an employee with this employment type. Update this employee in your payroll provider to continue.'
        }
        return (
          <Tooltip content={tooltipText} side='bottom' zIndex={10}>
            <div>{input}</div>
          </Tooltip>
        )
      }

      const toggleRowSelection = (rowId: string) => {
        setSelectedRowIds((prev: { [key: string]: boolean }) => ({
          ...prev,
          [rowId]: !prev[rowId], // Toggle the selection of the row
        }))
      }

      return (
        <input
          name='select-checkbox'
          type='checkbox'
          checked={row.getIsSelected()}
          onChange={() => toggleRowSelection(row.id)}
          aria-label='Select row'
          className='mx-auto h-4 w-4 rounded border-gray-300 text-thatch-purple focus:ring-thatch-purple'
        />
      )
    },
  }),

  columnHelper.accessor('id', {
    id: 'employee_id',
    header: 'Employee ID',
    cell: ({ row }) => {
      return row.getValue('id')
    },
    enableGlobalFilter: true,
  }),
  columnHelper.accessor('last_name', {
    id: 'employee_name',
    header: 'Employee',
    cell: ({ row }) => {
      const { first_name: firstName, last_name: lastName } = row.original
      const { openCreateOrEditEmployee } = useCreateOrEditEmployee()

      if (!firstName && !lastName) {
        return (
          <div className='flex items-center gap-2'>
            <span className='text-gray-500 italic'>Missing</span>
            <button
              onClick={(e) => {
                e.stopPropagation()
                openCreateOrEditEmployee(row.original as any)
              }}
              className='text-gray-900 hover:text-gray-700'
            >
              <Pencil size={14} />
            </button>
          </div>
        )
      }

      return `${firstName || ''} ${lastName || ''}`.trim()
    },
    enableGlobalFilter: true,
  }),
  columnHelper.accessor('email', {
    header: 'Email',
    cell: ({ row }) => {
      const { openCreateOrEditEmployee } = useCreateOrEditEmployee()
      const email = row.getValue('email')

      if (!email) {
        return (
          <div className='flex items-center gap-2'>
            <span className='text-gray-500 italic'>Missing</span>
            <button
              onClick={(e) => {
                e.stopPropagation()
                openCreateOrEditEmployee(row.original as any)
              }}
              className='text-gray-900 hover:text-gray-700'
            >
              <Pencil size={14} />
            </button>
          </div>
        )
      }

      return email
    },
    enableGlobalFilter: true
  }),
  columnHelper.accessor('start_date', {
    id: 'start_date',
    header: ({ column, table }) => {
      const sorted = table
        .getState()
        .sorting?.find(
          (el: { id: string; desc: boolean }) => el.id === column.id
        )

      const Icon = sorted?.desc ? ChevronDown : ChevronUp

      return (
        <span className='flex flex-wrap items-center w-fit hover:cursor-pointer'>
          <span className='pr-2'>Start date</span>
          <Icon
            className={clsx({ hidden: !sorted, 'shrink-0': true })}
            size={16}
          />
        </span>
      )
    },
    cell: ({ row }) => {
      if (row.getValue('start_date') === null) {
        return <div>---</div>
      }

      const dateStr: string = row.getValue('start_date')
      const [year, month, day] = dateStr.split('T')[0].split('-')
      const date: Date = new Date(
        parseInt(year, 10),
        parseInt(month, 10) - 1,
        parseInt(day, 10)
      )

      const formatter = new Intl.DateTimeFormat('en-US', {
        month: 'short', // "short" for abbreviated month name
        day: '2-digit',
        year: 'numeric',
      })

      const formattedDate = formatter.format(date)

      return <div>{formattedDate}</div>
    },
    enableSorting: true,
    sortingFn: (rowA, rowB) =>
      new Date(rowA.original.start_date).getTime() -
      new Date(rowB.original.start_date).getTime(),
    enableGlobalFilter: false,
  }),
  columnHelper.accessor('enrollment_status', {
    id: 'enrollment_status',
    filterFn: (row, _columnId, filterValuesList) => {
      if (filterValuesList.length > 0) {
        return filterValuesList.includes(row.getValue('enrollment_status'))
      } else {
        return true
      }
    },
    header: () => {
      const { filters, setFilters, setSelectedRowIds } = useContext(
        EmployeesTableContext
      )
      const numInviteStatusFiltersApplied: number = Object.values(
        filters
      ).reduce((acc, curr) => acc + (curr ? 1 : 0), 0)

      const handleEnrollmentStatusFilterChange = (callback: () => void) => {
        setSelectedRowIds({})
        callback()
      }

      return (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button aria-label='Customise options'>
              <div className='relative flex items-center space-x-1 hover:cursor-pointer'>
                <span>Status</span>
                <Filter size={12} />
                {numInviteStatusFiltersApplied > 0 && (
                  <span
                    className='absolute z-10 body-sm top-[-7.5px] right-[-7.5px] rounded-full bg-thatch-purple-secondary text-white flex items-center justify-center'
                    style={{
                      fontSize: '0.6rem',
                      height: '0.8rem',
                      width: '0.8rem',
                    }}
                  >
                    {numInviteStatusFiltersApplied}
                  </span>
                )}
              </div>
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className='DropdownMenuContent'
              sideOffset={5}
            >
              {Object.entries(mapFiltersToEnrollmentStatus(expiredStatusEnabled)).map(
                ([key, value]) => {
                  const { label } = value
                  return (
                    <DropdownMenu.CheckboxItem
                      key={key}
                      className='DropdownMenuCheckboxItem'
                      checked={filters[key as keyof EnrollmentStatusFilters]}
                      onCheckedChange={(checked) =>
                        handleEnrollmentStatusFilterChange(() =>
                          setFilters({ ...filters, [key]: checked })
                        )}
                    >
                      <DropdownMenu.ItemIndicator className='DropdownMenuItemIndicator'>
                        <CheckIcon size={16} color='#6941C6' />
                      </DropdownMenu.ItemIndicator>
                      {label}
                    </DropdownMenu.CheckboxItem>
                  )
                }
              )}
              <DropdownMenu.Arrow className='DropdownMenuArrow' />
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      )
    },
    cell: ({ row }) => {
      const enrollmentFilters = mapFiltersToEnrollmentStatus(expiredStatusEnabled)
      // Look up the enrollment status in the map to get the classes and label.
      const enrollmentStatus: keyof typeof enrollmentFilters = row.getValue('enrollment_status')
      // If the enrollment status is not found in the map, default to 'unknown'.
      const { classes, label, statusTip } =
        enrollmentFilters[enrollmentStatus] ||
        enrollmentFilters.unknown

      return (
        <div
          onMouseEnter={(e) => handleStatusHover(e, statusTip)}
          className={`inline-flex items-center rounded-md px-2 py-1 body-sm font-medium ${classes}`}
        >
          {label}
        </div>
      )
    },
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const valueA = rowA.original.enrollment_status
      const valueB = rowB.original.enrollment_status
      return (
        enrollmentStatusOrder.indexOf(valueA) -
        enrollmentStatusOrder.indexOf(valueB)
      )
    },
    enableGlobalFilter: false,
  }),
  columnHelper.accessor('employment_subtype', {
    id: 'employment_subtype',
    filterFn: (row, _columnId, filterValuesList) => {
      if (filterValuesList.length === 0) return true
      return filterValuesList.includes(row.getValue('employment_subtype'))
    },
    header: () => {
      const {
        employmentSubtypeOptions,
        employeeSubtypesToInclude,
        setEmployeeSubtypesToInclude,
        setSelectedRowIds,
      } = useContext(EmployeesTableContext)

      return (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button aria-label='Customise options'>
              <div className='relative flex items-center space-x-1 hover:cursor-pointer'>
                <span>Type</span>
                <Filter size={12} />
                {employeeSubtypesToInclude.length > 0 && (
                  <span
                    className='absolute z-10 body-sm top-[-7.5px] right-[-7.5px] rounded-full bg-thatch-purple-secondary text-white flex items-center justify-center'
                    style={{
                      fontSize: '0.6rem',
                      height: '0.8rem',
                      width: '0.8rem',
                    }}
                  >
                    {employeeSubtypesToInclude.length}
                  </span>
                )}
              </div>
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className='DropdownMenuContent'
              sideOffset={5}
            >
              {employmentSubtypeOptions.map((option) => (
                <DropdownMenu.CheckboxItem
                  key={option}
                  className='DropdownMenuCheckboxItem'
                  checked={employeeSubtypesToInclude.includes(option)}
                  onCheckedChange={(checked) => {
                    setSelectedRowIds({})
                    if (checked) {
                      setEmployeeSubtypesToInclude([
                        ...employeeSubtypesToInclude,
                        option,
                      ])
                    } else {
                      setEmployeeSubtypesToInclude(
                        employeeSubtypesToInclude.filter(
                          (subtype) => subtype !== option
                        )
                      )
                    }
                  }}
                >
                  <DropdownMenu.ItemIndicator className='DropdownMenuItemIndicator'>
                    <CheckIcon size={16} color='#6941C6' />
                  </DropdownMenu.ItemIndicator>
                  {EMPLOYMENT_SUBTYPE_DICT[option]?.label}
                </DropdownMenu.CheckboxItem>
              ))}
              <DropdownMenu.Arrow className='DropdownMenuArrow' />
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      )
    },
    cell: ({ row }) => {
      const { label, pillClass } =
        EMPLOYMENT_SUBTYPE_DICT[
          (row.getValue('employment_subtype') as string) || 'not_specified'
        ]
      return (
        <div
          className={`inline-flex items-center rounded-md px-2 py-1 body-sm font-medium ${pillClass}`}
        >
          {label}
        </div>
      )
    },
    enableGlobalFilter: false,
  }),
  columnHelper.display({
    id: 'actions',
    header: () => {
      return <div className='text-center'>Actions</div>
    },
    cell: ({ row }) => {
      const employee = row.original as any
      const { openCreateOrEditEmployee } = useCreateOrEditEmployee()

      return (
        <DropdownMenuContainer>
          <DropdownMenuTrigger className='flex items-center justify-center m-auto focus:outline-none'>
            <EllipsisVertical size={16} />
          </DropdownMenuTrigger>
          <DropdownMenuContent className='w-48'>
            <DropdownMenuItem
              onClick={() => {
                window.segment?.track('[ER] View employee clicked', {
                  employee_id: employee.id,
                  employee_name: employee.first_name + ' ' + employee.last_name,
                  employee_email: employee.personal_email || employee.work_email,
                })
                window.open(employersEmployees.show({ id: employee.id }))
              }}
            >
              <UserRound size={16} />
              <span>View employee</span>
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={() => {
                window.segment?.track('[ER] Add/edit employee form opened', {
                  action: 'edit_employee',
                  employee_id: employee.id,
                  employee_name: employee.first_name + ' ' + employee.last_name,
                  employee_email: employee.personal_email || employee.work_email,
                })
                openCreateOrEditEmployee(employee)
              }}
            >
              <UserRoundPen size={16} />
              <span>Edit employee</span>
            </DropdownMenuItem>

            <TerminateEmployeeMenuItem row={row} />

            <InviteEmployeeMenuItems row={row} />
          </DropdownMenuContent>
        </DropdownMenuContainer>
      )
    },
  }),
  ]
}

const allEnrollmentStatusFilters = {
  not_invited: {
    classes: 'bg-yellow-100 text-yellow-800',
    label: 'Not Invited',
    statusTip:
      'This employee has not received an email inviting them to begin benefits selection',
  },
  invite_pending: {
    classes: 'bg-fuchsia-100 text-fuchsia-600',
    label: 'Invite Pending',
    statusTip:
      'This employee will be sent an invitation email when the enrollment window begins',
  },
  pending_automated_invite: {
    classes: 'bg-fuchsia-100 text-fuchsia-600',
    label: 'Pending Automated Invite',
    statusTip:
      'This employee was marked as eligible during open enrollment renewals and will be invited automatically on November 5th, 2025. Please reach out to support@thatch.com if you need assistance.',
  },
  invited: {
    classes: 'bg-fuchsia-100 text-fuchsia-600',
    label: 'Invited',
    statusTip:
      'This employee has been sent an invitation email but has not setup their Thatch account',
  },
  in_progress: {
    classes: 'bg-cyan-100 text-cyan-800',
    label: 'Viewing Plans',
    statusTip:
      'This employee has setup their Thatch account and is viewing their health benefit options',
  },
  enrolled: {
    classes: 'bg-green-100 text-green-700',
    label: 'Enrolled',
    statusTip:
      'This employee has made their benefits selections and are now enrolled in their plans',
  },
  declined: {
    classes: 'bg-gray-100 text-gray-600',
    label: 'Declined',
    statusTip: 'This employee has declined their health benefits',
  },
  expired: {
    classes: 'bg-gray-100 text-gray-600',
    label: 'Invite expired',
    statusTip: "This employee didn't decline or enroll in coverage during their enrollment window.",
  },
  payroll_deactivated: {
    classes: 'bg-orange-100 text-orange-800',
    label: 'Payroll Deactivated',
    statusTip:
      "Please verify the employee's termination details and submit the termination. We will notify the employee of their next steps.",
  },
  cobra: {
    classes: 'bg-purple-100 text-purple-800',
    label: 'COBRA',
    statusTip: 'This employee has accepted their COBRA election.',
  },
  termination_scheduled: {
    classes: 'bg-gray-100 text-gray-600',
    label: 'Offboarding Scheduled',
    statusTip:
      'This employee has been terminated with a future effective date. They will be terminated EOD of the effective date.',
  },
  terminated: {
    classes: 'bg-gray-100 text-gray-600',
    label: 'Offboarded',
    statusTip: 'This employee is no longer employed at your company',
  },
  unknown: {
    classes: 'bg-gray-100 text-gray-800',
    label: 'Unknown',
    statusTip:
      "This employee's current status is unknown. Please reach out to support@thatch.com",
  },
}

const mapFiltersToEnrollmentStatus = (expiredStatusEnabled: boolean) => {
  if (!expiredStatusEnabled) {
    // Temporarily disabling eslint on this line until flag is fully rolled out -
    // this allows us to separate 'expired' from the rest of the status filters, without
    // having to make it the last filter in the dropdown list (we want Unknown to be last)
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      expired,
      ...enrollmentStatusFiltersWithoutExpired
    } = allEnrollmentStatusFilters
    return enrollmentStatusFiltersWithoutExpired
  } else {
    return allEnrollmentStatusFilters
  }
}

function EmployeesTableComponent ({
  employerName,
  hideAddEmployeeButton,
  isFullyOnboarded,
  isFullyOnboardedWithBankAccount,
  isDeniedEmployer,
  hasInvitedEmployeesBefore,
  hasPayrollIntegration,
  payrollIntegrationProvider,
  lastEmployeeSync,
  nextEmployeeSync,
  isConnectedToPayroll,
  planYearsAvailable,
  hasMembersWithActiveCoverageThisYear,
  allowedEmploymentSubtypesByYear,
  requiresEmployeeDeduplication,
  automaticDeductionsSetupComplete,
  automaticDeductionsEnabled,
  showAutomatedDeductionsNotice,
  supportsAutomaticDeductions,
  baaRestrictedColumnsEnabled,
  expiredStatusEnabled,
}: EmployeesTableProps) {
  const [allEmployees, setAllEmployees] = React.useState<EmployeeTableRow[]>([])
  const [selectedRowIds, setSelectedRowIds] =
    React.useState<SelectedRowIdsType>({})
  const [loading, setLoading] = React.useState(true)
  const [employeeToOffboard, setEmployeeToOffboard] = React.useState(null)

  const [filters, setFilters] = React.useState<EnrollmentStatusFilters>({
    not_invited: false,
    invite_pending: false,
    invited: false,
    pending_automated_invite: false,
    in_progress: false,
    enrolled: false,
    declined: false,
    payroll_deactivated: false,
    cobra: false,
    termination_scheduled: false,
    terminated: false,
    expired: false
  })

  const [employmentSubtypeOptions, setEmploymentSubtypeOptions] =
    React.useState<string[]>([])
  const [employeeSubtypesToInclude, setEmployeeSubtypesToInclude] =
    React.useState<string[]>([])

  const [selectedPlanYear, setSelectedPlanYear] = React.useState<number>(
    planYearsAvailable[0]
  )
  const defaultSorting = [
    { id: 'enrollment_status', desc: false },
    { id: 'employee_name', desc: false },
  ]

  const [sorting, setSorting] = React.useState<SortingState>(defaultSorting)

  // Parse date strings to Date objects if they exist
  const lastEmployeeSyncDate = lastEmployeeSync ? new Date(lastEmployeeSync) : null
  const nextEmployeeSyncDate = nextEmployeeSync ? new Date(nextEmployeeSync) : null

  const [activeStatus, setActiveStatus] = React.useState<string | null>(null)
  const [enrollmentRate, setEnrollmentRate] = React.useState<number>(0)
  const [statusCounts, setStatusCounts] = React.useState<Record<string, number>>({})

  const closeDialog = () => {
    setEmployeeToOffboard(null)
  }

  const allowancesSetUpForPlanYear = allowedEmploymentSubtypesByYear[selectedPlanYear] && allowedEmploymentSubtypesByYear[selectedPlanYear].length !== 0

  const table = useReactTable({
    columns: createColumns(expiredStatusEnabled, allowancesSetUpForPlanYear),
    data: allEmployees,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      sorting,
      rowSelection: selectedRowIds,
      columnVisibility: {
        // Include employee_id as a column to allow for filtering, but don't display it in the table
        employee_id: false,
      }
    },
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
    getRowId: (row) => row.id,
    onRowSelectionChange: setSelectedRowIds,
    enableRowSelection: true,
    globalFilterFn: (rows, columnId, filterValue) => {
      let columnValue = rows.getValue(columnId)
      if (columnId === 'employee_name') {
        const { first_name: firstName, last_name: lastName } = rows.original
        if (!firstName && !lastName) {
          columnValue = 'Missing'
        } else {
          columnValue = `${firstName || ''} ${lastName || ''}`
        }
      }
      if (typeof columnValue === 'string') {
        if (columnId === 'employee_id') {
          // Do an exact match for employee ID
          return columnValue === filterValue
        }
        return columnValue.toLowerCase().includes(filterValue.toLowerCase())
      }
      return false
    }
  })

  const fetchEmployees = async () => {
    if (!selectedPlanYear) return

    const params = new URLSearchParams([
      ['plan_year', selectedPlanYear.toString()],
    ]).toString()

    setLoading(true)
    try {
      // TODO: this method of making an API call is deprecated -- please use reactquery instead
      // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const response = await fetch(`/api/employees?${params}`, { // eslint-disable-line thatch/no-bare-fetch
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })

      if (response.status !== 200) throw new Error('Failed to fetch employees')
      const json = await response.json()
      const {
        employees,
        enrollment_rate: enrollmentRateValue = 0,
      } = json.data

      const statusCounts: Record<string, number> = {}
      for (const emp of employees) {
        const s = emp.enrollment_status as string
        statusCounts[s] = (statusCounts[s] ?? 0) + 1
      }

      const sanitizedEmployeesData = employees.map((employee: Record<string, any>) => ({
        ...employee,
        // Check if the employment subtype is expected, otherwise default to 'not_specified'.
        employment_subtype: EMPLOYMENT_SUBTYPE_DICT[employee.employment_subtype]
          ? employee.employment_subtype
          : 'not_specified',
        // Default to empty string for the following columns to ensure they aren't skipped by global search
        // (which skips columns where the first row value is null even if global filtering is enabled).
        email: employee.email || '',
        last_name: employee.last_name || '',
        first_name: employee.first_name || '',
        id: employee.id || '',
      }))
      setAllEmployees(sanitizedEmployeesData)
      setEnrollmentRate(enrollmentRateValue)
      setStatusCounts(statusCounts)
      setEmploymentSubtypeOptions([
        ...new Set(
          sanitizedEmployeesData.map((employee: any) =>
            EMPLOYMENT_SUBTYPE_DICT[employee.employment_subtype]
              ? employee.employment_subtype
              : 'not_specified'
          )
        ),
      ] as string[])
    } catch (e: any) {
      // TODO: Add error handling.
      console.error(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEmployees()
  }, [table, selectedPlanYear])

  useEffect(() => {
    // Use the hash of filters to generate a list of filters in array format to apply to the Tanstack table.
    const currentFilters = Object.entries(filters).reduce<string[]>(
      (acc, [key, value]) => {
        return value ? [...acc, key] : acc
      },
      []
    )

    table.getColumn('enrollment_status')?.setFilterValue(currentFilters)
    table
      .getColumn('employment_subtype')
      ?.setFilterValue(employeeSubtypesToInclude)
  }, [table, filters, employeeSubtypesToInclude])

  const handleStatusClick = (status: string) => {
    setActiveStatus((prevActive) => {
      const resetFilters = Object.fromEntries(
        Object.keys(filters).map(key => [key, false])
      ) as EnrollmentStatusFilters

      if (prevActive === status) {
        // Deselect if already active
        setFilters(resetFilters)
        return null
      } else {
        // If the combined "invited" tile is clicked,
        // set `invite_pending`, `invited`, and `pending_automated_invite` to true
        if (status === 'invited_combined') {
          setFilters({
            ...resetFilters,
            invite_pending: true,
            invited: true,
            pending_automated_invite: true,
          })
        } else {
          setFilters({
            ...resetFilters,
            [status]: true,
          })
        }
        return status
      }
    })
  }

  // This method resets the selected rows after an API call.
  const handleResetSelectedRows = () => {
    setSelectedRowIds({})
  }

  const isEmployeeInviteDisabled =
    Object.values(selectedRowIds).filter((value) => !!value).length === 0
  const selectedRowsIds = Object.entries(selectedRowIds).reduce<string[]>(
    (acc, [employeeId, isSelected]) =>
      isSelected ? [...acc, employeeId] : acc,
    []
  )
  const selectedRows = allEmployees.filter((employee) =>
    selectedRowsIds.includes(employee.id)
  )

  // Download click tracking handler
  const handleDownloadClick = () => {
    window.segment?.track('[ER] Download employee data clicked', {
      selected_employees_count: selectedRowsIds.length,
      total_employees_count: table.getFilteredRowModel().rows.length,
      plan_year: selectedPlanYear,
    })
  }

  const tableContext = {
    hideAddEmployeeButton,
    isFullyOnboarded,
    isFullyOnboardedWithBankAccount,
    isDeniedEmployer,
    hasPayrollIntegration,
    hasInvitedEmployeesBefore,
    planYearsAvailable,
    hasMembersWithActiveCoverageThisYear,
    selectedRowIds,
    setSelectedRowIds,
    allEmployees,
    allowedEmploymentSubtypesByYear,
    selectedPlanYear,
    handleResetSelectedRows,
    employeeSubtypesToInclude,
    setFilters,
    filters,
    setEmployeeToOffboard,
    employmentSubtypeOptions,
    setEmployeeSubtypesToInclude,
    fetchEmployees,
  }

  const getNoDataMessage = () => {
    // If filtering by employee name and no results
    if (table.getState().globalFilter) {
      return <>No employees match your current search parameters.</>
    }

    // If connected to payroll and first sync is in progress
    if (hasPayrollIntegration && isConnectedToPayroll && lastEmployeeSyncDate === null) {
      return (
        <>
          Waiting for your first employee sync to complete.
          We'll notify you by email once employees have been imported.
        </>
      )
    }

    if (hasPayrollIntegration && !isConnectedToPayroll) {
      return (
        <>
          Your payroll system is not connected.
          Please reconnect to import your employees.
        </>
      )
    }

    if (hasPayrollIntegration && isConnectedToPayroll && lastEmployeeSyncDate !== null) {
      return (
        <>
          No employees were synced from your payroll system.
          Once you add employees, they will be synced to Thatch within 24 hours.
        </>
      )
    }

    // Default case - no employees yet
    return (
      <>
        You don&apos;t have any employees yet. Invite your employees to
        select their health insurance plans on Thatch.
      </>
    )
  }

  return (
    <EmployeesTableContext.Provider value={tableContext}>
      <CreateOrEditEmployee
        fetchEmployees={fetchEmployees}
        handleAddAndInvite={() => { }}
      />
      <CreateOrEditEnrollmentEvent
        hasInvitedEmployeesBefore={hasInvitedEmployeesBefore}
        selectedPlanYear={selectedPlanYear}
        resetSelectedRowIds={handleResetSelectedRows}
        fetchEmployees={fetchEmployees}
      />
      <div className='flex justify-between gap-2 items-center mb-2'>
        <div className='flex items-center'>
          <h1 className='heading-lg'>Employees</h1>
        </div>
        {!hideAddEmployeeButton && (
          <div className='flex space-x-2'>
            <AddEmployeeButton
              isFullyOnboarded={isFullyOnboarded}
              hasPayrollIntegration={hasPayrollIntegration}
              isDeniedEmployer={isDeniedEmployer}
              fetchEmployees={fetchEmployees}
            />
          </div>
        )}
      </div>
      <div className='mb-6'>
        <SelectPlanYear
          planYearsAvailable={planYearsAvailable}
          selectedPlanYear={selectedPlanYear}
          handleSetPlanYear={(value: string) => {
            setSelectedPlanYear(parseInt(value))
            setSelectedRowIds({})
          }}
        />
      </div>

      <PayrollSyncStatusHeader
        isConnectedToPayroll={isConnectedToPayroll}
        lastEmployeeSync={lastEmployeeSyncDate}
        nextEmployeeSync={nextEmployeeSyncDate}
        payrollIntegrationProvider={payrollIntegrationProvider}
        hasPayrollIntegration={hasPayrollIntegration}
        requiresEmployeeDeduplication={requiresEmployeeDeduplication}
        automaticDeductionsSetupComplete={automaticDeductionsSetupComplete}
        automaticDeductionsEnabled={automaticDeductionsEnabled}
        showAutomatedDeductionsNotice={showAutomatedDeductionsNotice}
        supportsAutomaticDeductions={supportsAutomaticDeductions}
      />

      <EnrollmentRateTracker
        enrollmentRate={enrollmentRate}
        statusCounts={statusCounts}
        onStatusClick={handleStatusClick}
        activeStatus={activeStatus}
        showInviteBanner={
          !hasInvitedEmployeesBefore &&
          !allEmployees.some(employee =>
            (employee.last_invited_at != null && employee.last_invited_at !== '') ||
            isEmployeeInvitedOrInEnrollmentProcess(employee.enrollment_status)
          ) &&
          allEmployees.length > 0 && !isDeniedEmployer && allowancesSetUpForPlanYear
        }
        onSelectAllEmployees={() => {
          const newSelected: { [key: string]: boolean } = {}
          allEmployees.forEach((employee) => {
            if (
              employee.enrollment_status === 'not_invited' &&
              selectedPlanYear >= new Date().getFullYear() &&
              (allowedEmploymentSubtypesByYear[selectedPlanYear].includes(
                employee.employment_subtype
              ) ||
                !hasPayrollIntegration) &&
              (employeeSubtypesToInclude.length === 0 ||
                employeeSubtypesToInclude.includes(
                  employee.employment_subtype
                ))
            ) {
              newSelected[employee.id] = true
            }
          })
          setSelectedRowIds(newSelected)
        }}
        expiredStatusEnabled={expiredStatusEnabled}
      />

      <div className='flex flex-col'>
        <div className='sm:flex sm:flex-wrap sm:justify-between sm:gap-4 mb-6 order-last md:order-first'>
          <div className='mb-4 sm:mb-0 sm:w-1/3 lg:w-1/2 max-lg:flex-grow'>
            <TanstackTableGlobalSearch
              table={table}
              placeholder='Find employees'
            />
          </div>
          <div className='flex flex-wrap items-center flex-row gap-2 md:gap-x-4 shrink-0'>
            <EmployeeReportsDropdown
              selectedRowsIds={selectedRowsIds}
              filteredRows={table.getFilteredRowModel().rows}
              selectedPlanYear={selectedPlanYear}
              filters={filters}
              employeeSubtypesToInclude={employeeSubtypesToInclude}
              onDownloadClick={handleDownloadClick}
              showEnrollmentsReport={baaRestrictedColumnsEnabled}
            />
            <InviteEmployeeButton
              isEmployeeInviteDisabled={isEmployeeInviteDisabled}
              isFullyOnboarded={isFullyOnboarded}
              isFullyOnboardedWithBankAccount={isFullyOnboardedWithBankAccount}
              isDeniedEmployer={isDeniedEmployer}
              selectedRows={selectedRows}
              selectedRowsIds={selectedRowsIds}
              allowancesSetUpForPlanYear={allowancesSetUpForPlanYear}
            />
          </div>
        </div>
      </div>

      <TanstackTable
        table={table}
        isLoading={loading}
        setSorting={setSorting}
        defaultSortingState={defaultSorting}
        paginationLabelSingular='employee'
        noDataMessage={getNoDataMessage()}
      />

      <OffboardEmployeeDialog
        closeDialog={closeDialog}
        employerName={employerName}
        employee={employeeToOffboard}
      />
    </EmployeesTableContext.Provider>
  )
}

const AddEmployeeButton = ({
  isFullyOnboarded,
  hasPayrollIntegration,
  isDeniedEmployer
}: AddEmployeeButtonProps & {
  fetchEmployees: () => void
}) => {
  const { openCreateOrEditEmployee } = useCreateOrEditEmployee()
  const { breakpointIsAtLeast } = useBreakpoint()
  const buttonText = breakpointIsAtLeast('2xs') ? 'Add employee' : 'Add'

  if (!isFullyOnboarded || hasPayrollIntegration || isDeniedEmployer) {
    return (
      <AddEmployeeTooltip
        isFullyOnboarded={isFullyOnboarded}
        hasPayrollIntegration={hasPayrollIntegration}
        isDeniedEmployer={isDeniedEmployer}
      >
        <Button variant='secondary' disabled leftIcon={UserPlus}>
          {buttonText}
        </Button>
      </AddEmployeeTooltip>
    )
  }

  return (
    <Button
      variant='secondary'
      onClick={() => {
        window.segment?.track('[ER] Add/edit employee form opened', { action: 'add_employee' })
        openCreateOrEditEmployee()
      }}
      leftIcon={UserPlus}
    >
      {buttonText}
    </Button>
  )
}

const InviteEmployeeButton = ({
  isFullyOnboarded,
  isFullyOnboardedWithBankAccount,
  isEmployeeInviteDisabled,
  isDeniedEmployer,
  selectedRows,
  selectedRowsIds,
  allowancesSetUpForPlanYear
}: {
  isFullyOnboarded: boolean
  isFullyOnboardedWithBankAccount: boolean
  isEmployeeInviteDisabled: boolean
  isDeniedEmployer: boolean
  selectedRows: EmployeeTableRow[]
  selectedRowsIds: string[]
  allowancesSetUpForPlanYear: boolean
}) => {
  const { openCreateOrEditEnrollmentEvent } = useCreateOrEditEnrollmentEvent()

  if (!isFullyOnboarded || !isFullyOnboardedWithBankAccount || isEmployeeInviteDisabled) {
    return (
      <InviteEmployeeTooltip
        isFullyOnboarded={isFullyOnboarded}
        isFullyOnboardedWithBankAccount={isFullyOnboardedWithBankAccount}
        isEmployeeInviteDisabled={isEmployeeInviteDisabled}
        isDeniedEmployer={isDeniedEmployer}
        allowancesSetUpForPlanYear={allowancesSetUpForPlanYear}
      >
        <Button variant='primary' disabled leftIcon={Send}>
          Invite selected{' '}
          {selectedRowsIds.length > 0 && `(${selectedRowsIds.length})`}
        </Button>
      </InviteEmployeeTooltip>
    )
  }

  return (
    <Button
      variant='primary'
      onClick={() => {
        window.segment?.track('[ER] Invite employees form opened', {
          selected_employees_count: selectedRowsIds.length,
          selected_employees: selectedRowsIds,
        })

        openCreateOrEditEnrollmentEvent(selectedRows)
      }}
      leftIcon={Send}
    >
      Invite selected{' '}
      {selectedRowsIds.length > 0 && `(${selectedRowsIds.length})`}
    </Button>
  )
}

const EmployeesTable = (props: EmployeesTableProps) => (
  <CreateOrEditEnrollmentEventProvider>
    <CreateOrEditEmployeeProvider>
      <EmployeesTableComponent {...props} />
    </CreateOrEditEmployeeProvider>
  </CreateOrEditEnrollmentEventProvider>
)

export default EmployeesTable
